import { useRef, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, useAuthUi } from './AuthProvider.js';
import { useDeferredFlag } from './useDeferredFlag.js';

/**
 * Route guards compartidos (extraídos de speak). La UI (splash de marca,
 * pantalla de error de conexión) y las rutas login/home las aporta cada app
 * vía la prop `ui` del `<AuthProvider>`.
 *
 * - Mientras la hidratación inicial de `me()` está en vuelo → splash de marca,
 *   pero DIFERIDO: si la sesión hidrata rápido (<160ms, lo normal contra el
 *   backend local) el splash NO aparece y se pasa directo a la pantalla —
 *   evita el "doble loader" (splash de auth + loader propio de la pantalla).
 * - Resuelto sin user → redirect a `ui.loginPath` (replace, para que back no
 *   loopee dentro de la ruta protegida).
 * - Boot falló por transporte/5xx (NO 401) → `ui.connectionError` con
 *   reintento, en vez de patear al login: la cookie puede seguir siendo válida
 *   y un deploy del backend no debe desloguear visualmente a todos los que
 *   recargan.
 */

const SPLASH_DELAY_MS = 160;

interface GuardProps {
  children: ReactNode;
}

export function RequireAuth({ children }: GuardProps) {
  const { user, loading, bootError, retry } = useAuth();
  const ui = useAuthUi();
  const showSplash = useDeferredFlag(loading, SPLASH_DELAY_MS);

  if (loading) return showSplash ? <>{ui.splash}</> : null;
  if (bootError && !user) return <>{ui.connectionError(() => void retry())}</>;
  if (!user) return <Navigate to={ui.loginPath} replace />;
  return <>{children}</>;
}

/**
 * Guard inverso para las pantallas públicas de auth (login): con sesión
 * resuelta redirige a `ui.homePath`; sin sesión renderiza la pantalla.
 *
 * La decisión se CONGELA al resolver la hidratación: si el user aparece
 * después (un `otpVerify` DENTRO de la pantalla de login), el guard no emite
 * su `<Navigate a home>` — la pantalla navega ella misma según `isNewUser`
 * (onboarding vs home). Sin el congelamiento, ambos navigate corren en
 * paralelo y el del guard puede pisar el destino del login (carrera observada
 * en spar 2026-07-04: el signup nuevo aterrizaba en /home en vez de
 * /onboarding).
 */
export function RedirectIfAuthed({ children }: GuardProps) {
  const { user, loading } = useAuth();
  const ui = useAuthUi();
  const showSplash = useDeferredFlag(loading, SPLASH_DELAY_MS);
  const authedAtHydration = useRef<boolean | null>(null);
  if (!loading && authedAtHydration.current === null) {
    authedAtHydration.current = user !== null;
  }

  if (loading) return showSplash ? <>{ui.splash}</> : null;
  if (authedAtHydration.current) return <Navigate to={ui.homePath} replace />;
  return <>{children}</>;
}

/**
 * Resolutor de `/` y `*`: con sesión → home, sin sesión → login. El loading
 * visible lo maneja el guard de la ruta destino (acá `null` para no duplicar
 * splash).
 */
export function RootRedirect() {
  const { user, loading } = useAuth();
  const ui = useAuthUi();
  if (loading) return null;
  return <Navigate to={user ? ui.homePath : ui.loginPath} replace />;
}
